import React from 'react';
import { ToastContext, Dropdown } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import useAppContext from '../../../../context/useAppContext';
import PropTypes from 'prop-types';
import ActionOnRatesModal from './actionOnRatesModal';
import RateModel from '../../../../models/rateModel';

export function AddToGroupActionModal({ selectedRates, showModal = false, rates, groups, model, onSave, onClose }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [{ event, organization }] = useAppContext();

    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const mapToGroups = React.useCallback(group => ({ key: group.pk, text: group.name ?? group?.group_template?.name, value: group.pk }), []);

    const optionsGroups = React.useMemo(() => {
        let firstOption = [
            {
                key: `no_group`,
                text: t(`ticket.rates.infos.noGroups`),
                value: `no_group`
            }
        ];

        return firstOption.concat(groups?.filter(group => group !== null).map(mapToGroups));
    }, [groups, mapToGroups]);

    const addRatesToGroup = React.useCallback(
        groupId => {
            setIsSubmitting(true);
            //Sort By display_order to display the new rates at the end of their new group but order with their previous display_order
            model.sort(selectedRates);
            //Get size of their next group to set their next display_order
            let lengthGroup = rates.filter(rate => rate.group === groupId).length || 0;
            //The group where the rates come from and we have to edit the display_order of the rates that belongs to it
            let groupsToSort = new Set();
            selectedRates.forEach((rate, i) => {
                if (rate instanceof RateModel) {
                    //add key form_id , so that the backend does not delete the linked form
                    rate.form_id = rate.form;
                }
                groupsToSort.add(rate.group);
                rate.group = groupId !== 'no_group' ? groupId : null;
                rate.translations = { name: {} };
                // Set their next display_order with the size of the group
                rate.display_order = lengthGroup + i + 1;
                rate.relays = rate.relays?.map(relayed => relayed.id);
            });
            //Set the new display_order of rates stayed in the previous group
            let editedRates = [];
            //Get all rates without selectedRates
            let selectedRatesId = selectedRates.map(rate => rate.id);
            let tmpRates = rates.filter(rate => !selectedRatesId.includes(rate.id));
            groupsToSort.forEach(groupId => {
                //Find rates belonging to the group
                let group = tmpRates.filter(rate => rate.group === groupId);
                //Set the new display order if different
                group.forEach((rate, i) => {
                    if (rate.display_order !== i + 1) {
                        rate.display_order = i + 1;
                        rate.translations = { name: {} }; //handle translations issue
                        rate.relays = rate.relays?.map(relayed => relayed.id);
                        editedRates.push(rate);
                    }
                });
            });
            model
                .save(
                    [...selectedRates, ...editedRates],
                    {
                        organizationId: organization.pk,
                        eventId: event ? event.id : null
                    },
                    { skip_chart_validation: true }
                )
                .then(() => {
                    toast.success(t(`ticket.rates.updateGroupSuccess`));
                    onSave();
                })
                .catch(e => {
                    toast.error(t(`ticket.rates.updateGroupError`));
                })
                .finally(() => {
                    setIsSubmitting(false);
                    onClose();
                });
        },
        [selectedRates, rates, organization, event, onSave, onClose, model]
    );
    return (
        <ActionOnRatesModal
            type="group"
            selectedId={selectedRates.length === 1 ? selectedRates[0].group : null}
            onSave={groupId => addRatesToGroup(groupId)}
            onClose={onClose}
            options={optionsGroups}
            showModal={showModal}
            title={t(`ticket.rates.addRatesGroup`)}
            isSubmitting={isSubmitting}
        />
    );
}

export function GroupAction({ objects: selectedRates, groups, withModal = true, onClick = () => {}, ...props }) {
    const [showModal, setShowModal] = React.useState(false);

    const { t } = useTranslation();

    return (
        <>
            {withModal && showModal && (
                <AddToGroupActionModal
                    selectedRates={selectedRates}
                    groups={groups}
                    showModal={showModal}
                    onClose={() => {
                        setShowModal(false);
                    }}
                    {...props}
                />
            )}
            <Dropdown.Item
                disabled={!groups?.length > 0}
                onClick={() => {
                    onClick();
                    setShowModal(true);
                }}
            >
                {t('ticket.rates.addToGroup')}
            </Dropdown.Item>
        </>
    );
}

GroupAction.propTypes = {
    objects: PropTypes.array, //required
    rates: PropTypes.array.isRequired,
    groups: PropTypes.array.isRequired,
    model: PropTypes.func,
    onSave: PropTypes.func.isRequired
};
